// lib/auth-stack.ts
//
// Controls whether this deployment routes ALL auth through raauth (the v2 stack)
// or keeps the legacy v1 NextAuth flows.
//
// Enabled per-environment via the public `NEXT_PUBLIC_RAAUTH_ONLY` flag so it is
// readable in BOTH server code (middleware, auth.config) and client components
// (header/profile logout) — `APP_ENV` is server-only and cannot be read in the
// browser bundle. Set `NEXT_PUBLIC_RAAUTH_ONLY=true` on the dev server only;
// leave it unset (or "false") in production, which keeps v1 behavior unchanged.

import { Routes } from "@/lib/routes"

export const RAAUTH_ONLY = process.env.NEXT_PUBLIC_RAAUTH_ONLY === "true"

/**
 * Where to send someone who needs to sign in before reaching `callbackPath`.
 *
 * Under RAAUTH_ONLY this targets /unified/auth directly. Linking to /unified/login instead
 * reaches the same page via two extra round trips — middleware's RAAUTH_REDIRECTS rewrite,
 * then Next's `trailingSlash` canonicalisation.
 *
 * Lives here (not in lib/auth-guards.ts, which is server-only) so client components and
 * edge middleware can build the same URL.
 */
export function loginPathFor(callbackPath: string): string {
   const base = RAAUTH_ONLY ? Routes.client.unifiedAuth.getPath() : Routes.client.unifiedLogin.getPath()
   return `${base}?callbackUrl=${encodeURIComponent(callbackPath)}`
}

/**
 * Where to send someone who wants to create an account, before returning to `callbackPath`.
 *
 * Under RAAUTH_ONLY this is the SAME delegated OIDC entry point as login — /unified/auth —
 * with `mode=signup` selecting the signup form. Hit without a `uid` it self-initiates the
 * interaction (`screen_hint=signup`), and raauth delegates back to
 * /unified/auth?uid=…&mode=signup, which is what makes raauth drop the shared SSO cookie.
 *
 * Deliberately NOT /v2/unified/signup: that page posts the account straight to RA's own
 * signup API and never establishes the raauth browser session, so sister sites wouldn't
 * silently pick it up. It stays reachable as the direct-URL landing page, but nothing in
 * the app's chrome should link at it under RAAUTH_ONLY.
 */
export function signupPathFor(callbackPath: string): string {
   if (RAAUTH_ONLY) {
      const params = new URLSearchParams({ mode: "signup", callbackUrl: callbackPath })
      return `${Routes.client.unifiedAuth.getPath()}?${params.toString()}`
   }
   // Legacy v1 signup. The Routes entry is slash-less because it also feeds `canonical`
   // metadata; `trailingSlash: true` in next.config, so normalise or the link eats a 308.
   return `${Routes.client.unifiedSignup.getPath()}/?callbackUrl=${encodeURIComponent(callbackPath)}`
}

// Exact legacy -> v2 route mapping used by middleware to redirect the auth entry
// points to their raauth-backed equivalents. Matching is EXACT (trailing slash
// normalized by the caller), never prefix-based, so the OIDC-delegation and
// support pages that have no v2 equivalent are left untouched:
//   /unified/auth, /unified/auth-help, /unified/magic-link/confirm (and the
//   legacy /unified/magic-link/[uid] forwarder),
//   /unified/confirm-request, /unified/callback, /unified/error,
//   /unified/signup/success
export const RAAUTH_REDIRECTS: Record<string, string> = {
   // Login goes straight to the delegated OIDC page: /unified/auth shows the form
   // when raauth delegates back with a `uid`, and self-initiates the interaction
   // when hit without one — so there's no separate /v2/unified/login launcher hop.
   "/unified/login": "/unified/auth",
   "/unified/signup": "/v2/unified/signup",
   "/unified/password-reset": "/v2/unified/password-reset",
   "/unified/password-reset/email": "/v2/unified/password-reset/email",
   "/unified/magic-link": "/v2/unified/magic-link",
}
